"use client";

import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import CoverPlate from "@/src/components/common/broadsheet/cover-plate";
import EntryTitle from "@/src/components/common/broadsheet/entry-title";
import Kicker from "@/src/components/common/broadsheet/kicker";
import MetaLine from "@/src/components/common/broadsheet/meta-line";
import CategoryButton from "@/src/components/main-site/home/journals-section/category-button/category-button";
import {
  doubleRule,
  FONT_DISPLAY,
  FONT_TEXT,
  hairlineTop,
  HEADING_SIZE,
  SHEET_GUTTER,
  spineForCategory,
} from "@/src/components/common/broadsheet/tokens";
import { simpleDateFormatter } from "@/src/utils/dateFormatter";
import { isValidDate } from "@/src/utils/is-valid-date";

const ALL = "all";

const COVER_WIDTH = 108;
const COVER_HEIGHT = 144;
const COVER_WIDTH_XS = 84;
const COVER_HEIGHT_XS = 112;

const yearOf = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return isValidDate(date) ? date.getFullYear() : null;
};

const dayMonth = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (!isValidDate(date)) return null;
  return simpleDateFormatter(value, { month: "short", day: "2-digit" });
};

export default function NewsPage({
  journal,
  issuesData,
  posts = [],
}: {
  journal: any;
  issuesData: any;
  posts?: any[];
}) {
  const [activeYear, setActiveYear] = useState<string>(ALL);

  const issues = useMemo(
    () =>
      ((issuesData ?? []) as any[]).filter(
        (issue) =>
          issue?.id?.toString() !== journal.default_issue_id?.toString(),
      ),
    [issuesData, journal.default_issue_id],
  );

  const groups = useMemo(() => {
    const byYear = new Map<number, { year: number; posts: any[] }>();

    issues.forEach((issue) => {
      const year = yearOf(issue?.publish_date);
      if (year === null) return;
      if (!byYear.has(year)) byYear.set(year, { year, posts: [] });
    });

    const issueYear = new Map<number, number>();
    issues.forEach((issue) => {
      const year = yearOf(issue?.publish_date);
      if (year !== null && issue?.id != null) issueYear.set(issue.id, year);
    });

    posts.forEach((post) => {
      const year = issueYear.get(post?.issue_id) ?? yearOf(post?.publish_date);
      if (year === null) return;
      if (!byYear.has(year)) byYear.set(year, { year, posts: [] });
      byYear.get(year)!.posts.push(post);
    });

    return Array.from(byYear.values())
      .map((group) => ({
        ...group,
        posts: group.posts.sort(
          (a, b) =>
            new Date(b.publish_date ?? 0).getTime() -
            new Date(a.publish_date ?? 0).getTime(),
        ),
      }))
      .sort((a, b) => b.year - a.year);
  }, [issues, posts]);

  const visible =
    activeYear === ALL
      ? groups
      : groups.filter((group) => String(group.year) === activeYear);

  const postHref = (post: any) =>
    journal.is_main_site
      ? `/posts/${post.slug}`
      : `/journals/${journal.path}/posts/${post.slug}`;

  const total = groups.reduce((sum, group) => sum + group.posts.length, 0);

  return (
    <Box sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}>
      <Box
        sx={{ pt: { xs: 1, md: 1.5 }, pb: { xs: 0.75, md: 1 }, ...doubleRule }}
      >
        <Kicker sx={{ mb: 0.25 }}>The archive</Kicker>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { lg: "flex-end" },
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: HEADING_SIZE.page,
                lineHeight: 1.05,
                letterSpacing: "0.01em",
                color: "custom.ink",
              }}
            >
              News &amp; Posts
            </Typography>
            <Typography
              component="p"
              sx={{
                m: 0,
                mt: 0.25,
                fontFamily: FONT_TEXT,
                fontSize: { xs: "0.9375rem", md: "1rem" },
                lineHeight: 1.6,
                color: "custom.inkMuted",
                maxWidth: "56ch",
              }}
            >
              Announcements, editorial updates and publishing news — every post,
              by year.
            </Typography>
          </Box>
          <MetaLine
            sx={{ flex: "none" }}
            items={[`${total} posts`, `${groups.length} years`]}
          />
        </Box>
      </Box>

      {groups.length > 1 ? (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.25,
            py: 0.75,
            borderBottom: "1px solid",
            borderColor: "custom.ruleHairline",
          }}
        >
          <Kicker component="span" sx={{ mr: 0.375 }}>
            Year
          </Kicker>
          <CategoryButton
            onClick={() => setActiveYear(ALL)}
            aria-pressed={activeYear === ALL}
          >
            All
          </CategoryButton>
          {groups.map((group) => (
            <CategoryButton
              key={group.year}
              onClick={() => setActiveYear(String(group.year))}
              aria-pressed={activeYear === String(group.year)}
            >
              {group.year}
            </CategoryButton>
          ))}
        </Box>
      ) : null}

      {visible.map((group) => (
        <Box
          key={group.year}
          component="section"
          aria-labelledby={`year-${group.year}`}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "9rem 1fr" },
            columnGap: 2.5,
            rowGap: 0.5,
            py: { xs: 1, md: 1.25 },
            ...hairlineTop,
            "& > *": { minWidth: 0 },
          }}
        >
          <Box>
            <Typography
              component="h2"
              id={`year-${group.year}`}
              sx={{
                m: 0,
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: HEADING_SIZE.page,
                lineHeight: 1,
                color: spineForCategory(String(group.year)),
              }}
            >
              {group.year}
            </Typography>
            <MetaLine
              sx={{ mt: 0.125 }}
              items={[
                `${group.posts.length} ${
                  group.posts.length === 1 ? "post" : "posts"
                }`,
              ]}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                xl: "repeat(2, minmax(0, 1fr))",
              },
              columnGap: 2.5,
              "& > *": { minWidth: 0 },
            }}
          >
            {group.posts.length === 0 ? (
              <MetaLine items={["No posts recorded for this year."]} />
            ) : (
              group.posts.map((post) => (
                <Box
                  key={post.id}
                  component="article"
                  sx={{
                    display: "flex",
                    gap: 0.75,
                    py: 0.75,
                    ...hairlineTop,
                  }}
                >
                  <CoverPlate
                    src={post.image_url}
                    alt={post.image_alt ?? ""}
                    width={COVER_WIDTH}
                    height={COVER_HEIGHT}
                    spine={spineForCategory(String(group.year))}
                    fit="contain"
                    sx={{
                      width: { xs: COVER_WIDTH_XS, sm: COVER_WIDTH },
                      height: { xs: COVER_HEIGHT_XS, sm: COVER_HEIGHT },
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <MetaLine items={[dayMonth(post.publish_date)]} />
                    <EntryTitle
                      href={postHref(post)}
                      size="md"
                      sx={{ mt: 0.125 }}
                    >
                      {post.title}
                    </EntryTitle>
                    {post.deck ? (
                      <Typography
                        component="p"
                        sx={{
                          m: 0,
                          mt: 0.1875,
                          fontFamily: FONT_TEXT,
                          fontStyle: "italic",
                          fontSize: "0.8125rem",
                          lineHeight: 1.45,
                          color: "custom.inkMuted",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {post.deck}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
